'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import { connect } from 'react-redux';
import emptyFunction from 'react-utils/emptyFunction';
import PropTypes from 'prop-types';
import { bindAll } from 'underscore';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import text from 'I18n/utils/unescapedText';
import UIButton from 'UIComponents/button/UIButton';
import UIDropdownDivider from 'UIComponents/dropdown/UIDropdownDivider';
import UIFileButton from 'UIComponents/button/UIFileButton';
import { FINDER_FILE_CREATE, FINDER_FOLDER_CREATE, FINDER_THEMES_CREATE, FINDER_FUNCTIONS_CREATE, FINDER_FUNCTIONS_DEPLOY } from 'DesignData/actions/ActionTypes';
import { createFolder } from 'DesignData/actions/finder/Folders';
import * as FilesActions from 'DesignData/actions/finder/Files';
import { DESIGN_ROOT } from 'DesignData/enums/FolderSubcategories';
import { FolderPropTypes } from 'DesignData/propTypes/finder';
import { makeGetFolderProps, makeGetFolderPermissions, getFolder } from 'DesignData/selectors/finder';
import { getIsProjectsUngated } from 'DesignData/selectors/gates';
import { getHasServerlessFunctions } from 'DesignData/selectors/auth';
import NewFolderForm from 'DesignFinderUI/components/forms/NewFolderForm';
import FinderActionsList from 'DesignFinderUI/components/actions/FinderActionsList';
import withFileMenuActionsList from 'DesignFinderUI/components/actions/withFileMenuActionsList';
import { FILE_UPLOAD_MEDIA_TYPES } from 'DesignData/constants/Files';
import { compose } from 'redux';

const i18nKey = key => `DesignFinderUI.actions.NewAssetActions.${key}`;

const i18nKeyUpload = key => `DesignFinderUI.actions.UploadAssetActions.${key}`;

const i18nDeployFunctionKey = key => `DesignFinderUI.actions.DeployFunctionActions.${key}`;

function makeMapStateToProps() {
  const getParentFolderProps = makeGetFolderProps();
  const getFolderPermisssions = makeGetFolderPermissions();
  return (state, ownProps) => {
    const {
      folderId,
      folder
    } = ownProps;
    const folderPermissions = getFolderPermisssions(state, {
      folderId
    });
    const {
      name: folderName,
      subcategory: folderSubcategory,
      parentFolderId,
      isReadOnly,
      isDefault
    } = getFolder(state, {
      folderId
    }) || {};
    const {
      folderSubcategory: parentFolderSubcategory
    } = getParentFolderProps(state, {
      folderId: parentFolderId
    });
    const isFolderReadonly = !!isReadOnly || !!isDefault;
    return {
      canCreateFile: !isFolderReadonly && folderPermissions[FINDER_FILE_CREATE],
      canCreateFolder: !isFolderReadonly && folderPermissions[FINDER_FOLDER_CREATE],
      canCreateTheme: folderPermissions[FINDER_THEMES_CREATE],
      canCreateFunction: folderPermissions[FINDER_FUNCTIONS_CREATE],
      canDeployFunction: folderPermissions[FINDER_FUNCTIONS_DEPLOY],
      folder,
      folderName,
      folderSubcategory,
      parentFolderSubcategory,
      hasServerlessFunctions: getHasServerlessFunctions(state),
      projectsUngated: getIsProjectsUngated(state)
    };
  };
}

const mapDispatchToProps = {
  createFolder,
  uploadFiles: FilesActions.uploadFiles,
  validateFileUpload: FilesActions.validateFileUpload
};
export default function withNewAssetActions(WrappedComponent) {
  class HighOrderNewAssetActions extends Component {
    constructor(props) {
      super(props);
      bindAll(this, ['handleDeployFunctionClick', 'handleDeployProjectClick', 'handleNewFileClick', 'handleNewFolderCallback', 'handleNewFolderClick', 'handleNewThemeClick', 'handleNewFunctionClick', 'handleNewProjectClick', 'handleUploadFile', 'renderButtonText', 'renderNewFileButton', 'renderNewFolderButton', 'renderNewThemeButton', 'renderNewFolderForm', 'renderList', 'renderForm']);
      const {
        open,
        use
      } = props;
      this.state = {
        newFolderIsShown: false,
        open: open !== null ? open : use === 'default'
      };
    }

    componentDidUpdate(prevProps, prevState) {
      const {
        open,
        newFolderIsShown
      } = prevState;
      const {
        newFolderIsShown: nextNewFolderIsShown
      } = this.state;
      const {
        onNewAssetFormShown
      } = prevProps;
      const {
        open: nextOpen
      } = this.props;

      if (newFolderIsShown !== nextNewFolderIsShown) {
        onNewAssetFormShown(nextNewFolderIsShown);
      }

      if (nextOpen !== open) {
        let updatedState = {
          open: nextOpen
        };

        if (nextOpen) {
          updatedState = Object.assign({}, updatedState, {
            newFolderIsShown: false
          });
        }

        this.setState(updatedState);
      }
    }

    handleNewFileClick(evt) {
      const {
        onClose,
        onNewFileClick
      } = this.props;
      onNewFileClick(evt);
      onClose();
    }

    handleNewFolderCallback(data) {
      const {
        folderId,
        createFolder: createNewFolder,
        onClose
      } = this.props;

      if (data && data.folderName) {
        createNewFolder(data.folderName, folderId);
      }

      onClose();
    }

    handleNewFolderClick(evt) {
      evt.stopPropagation();
      this.setState({
        newFolderIsShown: true
      });
    }

    handleNewThemeClick(evt) {
      const {
        onClose,
        onNewThemeClick
      } = this.props;
      onNewThemeClick(evt);
      onClose();
    }

    handleNewFunctionClick(evt) {
      const {
        onNewFunctionClick,
        onClose
      } = this.props;
      onNewFunctionClick(evt);
      onClose();
    }

    handleNewProjectClick(evt) {
      const {
        onNewProjectClick,
        onClose
      } = this.props;
      onNewProjectClick(evt);
      onClose();
    }

    handleUploadFile(evt) {
      const {
        folder,
        onClose,
        uploadFiles,
        validateFileUpload
      } = this.props;
      const fileList = Array.from(evt.target.files).filter(validateFileUpload);
      uploadFiles(fileList, folder);
      onClose();
    }

    handleDeployFunctionClick(evt) {
      const {
        onDeployFunctionClick,
        onClose
      } = this.props;
      onDeployFunctionClick(evt);
      onClose();
    }

    handleDeployProjectClick(evt) {
      const {
        onDeployProjectClick,
        onClose
      } = this.props;
      onDeployProjectClick(evt);
      onClose();
    }

    renderButtonText(assetType, props, customI18nKey = i18nKey) {
      const {
        folderName,
        folderSubcategory
      } = props;
      let message = 'default';
      let options = {
        assetType
      };

      if (folderSubcategory !== DESIGN_ROOT) {
        message = 'folder';
        options = Object.assign({}, options, {
          folderName: I18n.SafeString(folderName)
        });
      }

      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: customI18nKey(`buttons.${message}`),
        options: options
      });
    }

    renderNewFileButton(props) {
      const {
        canCreateFile
      } = props;
      const fileTypeText = text(i18nKey('assets.file'));
      const renderedButtonText = this.renderButtonText(fileTypeText, props);
      return /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "new-file-button",
        disabled: !canCreateFile,
        onClick: this.handleNewFileClick,
        use: "link",
        children: renderedButtonText
      });
    }

    renderNewFolderButton(props) {
      const {
        canCreateFolder
      } = props;
      const renderedButtonText = this.renderButtonText(text(i18nKey('assets.folder')), props);
      return /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "new-folder-button",
        disabled: !canCreateFolder,
        onClick: this.handleNewFolderClick,
        use: "link",
        children: renderedButtonText
      });
    }

    renderNewFolderForm() {
      return /*#__PURE__*/_jsx(NewFolderForm, {
        callback: this.handleNewFolderCallback
      });
    }

    renderNewThemeButton(props) {
      const {
        canCreateTheme
      } = props;
      const renderedButtonText = this.renderButtonText(text(i18nKey('assets.theme')), props);
      return /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "new-theme-button",
        disabled: !canCreateTheme,
        onClick: this.handleNewThemeClick,
        use: "link",
        children: renderedButtonText
      });
    }

    renderNewFunctionButton(props) {
      const {
        canCreateFunction,
        hasServerlessFunctions
      } = props;

      if (!hasServerlessFunctions) {
        return null;
      }

      const renderedButtonText = this.renderButtonText(text(i18nKey('assets.function')), props);
      return /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "new-function-button",
        disabled: !canCreateFunction,
        onClick: this.handleNewFunctionClick,
        use: "link",
        children: renderedButtonText
      });
    }

    renderUploadFileButton(props) {
      const {
        canCreateFile
      } = props;
      const fileTypeText = text(i18nKeyUpload('assets.file'));
      const renderedButtonText = this.renderButtonText(fileTypeText, props, i18nKeyUpload);
      return /*#__PURE__*/_jsx(UIFileButton, {
        accept: FILE_UPLOAD_MEDIA_TYPES,
        "data-test-id": "upload-file-button",
        disabled: !canCreateFile,
        multiple: true,
        onChange: this.handleUploadFile,
        children: renderedButtonText
      });
    }

    renderDeployFunctionButton(props) {
      const {
        canDeployFunction,
        hasServerlessFunctions
      } = props;

      if (!hasServerlessFunctions || !canDeployFunction) {
        return null;
      }

      const renderedButtonText = this.renderButtonText(text(i18nDeployFunctionKey('buttons.default')), props, i18nDeployFunctionKey);
      return /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "deploy-function-button",
        onClick: this.handleDeployFunctionClick,
        use: "link",
        children: renderedButtonText
      });
    }

    renderList(props, state) {
      const {
        use,
        fileMenuActionsList
      } = props;
      const {
        newFolderIsShown
      } = state;

      if (newFolderIsShown) {
        return null;
      }

      const renderedNewFileButton = this.renderNewFileButton(props);
      const renderedNewFolderButton = this.renderNewFolderButton(props);
      const renderedNewThemeButton = this.renderNewThemeButton(props);
      const renderedNewFunctionButton = this.renderNewFunctionButton(props);
      const renderedUploadFileButton = this.renderUploadFileButton(props);
      const renderedDeployFunctionButton = this.renderDeployFunctionButton(props);
      return /*#__PURE__*/_jsxs(FinderActionsList, {
        style: {
          minWidth: 135
        },
        use: use,
        children: [renderedNewFileButton, renderedNewFolderButton, renderedNewThemeButton, renderedNewFunctionButton, /*#__PURE__*/_jsx(UIDropdownDivider, {}), renderedUploadFileButton, renderedDeployFunctionButton, fileMenuActionsList.length > 0 && /*#__PURE__*/_jsx(UIDropdownDivider, {}), fileMenuActionsList]
      });
    }

    renderForm(props, state) {
      const {
        newFolderIsShown
      } = state;

      if (newFolderIsShown) {
        return this.renderNewFolderForm();
      }

      return null;
    }

    render() {
      const {
        props,
        state
      } = this;
      const renderedList = this.renderList(props, state);
      const renderedForm = this.renderForm(props, state);
      return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, {
        NewAssetActionsList: renderedList,
        NewAssetForm: renderedForm
      }));
    }

  }

  HighOrderNewAssetActions.propTypes = {
    canCreateFile: PropTypes.bool,
    canCreateFolder: PropTypes.bool,
    createFolder: PropTypes.func.isRequired,
    uploadFiles: PropTypes.func.isRequired,
    validateFileUpload: PropTypes.func.isRequired,
    folder: PropTypes.object,
    folderId: FolderPropTypes.folderId,
    folderName: FolderPropTypes.folderName,
    folderSubcategory: FolderPropTypes.folderSubcategory,
    onClose: PropTypes.func,
    onNewAssetFormShown: PropTypes.func,
    onNewFileClick: PropTypes.func,
    onNewThemeClick: PropTypes.func,
    onNewFunctionClick: PropTypes.func,
    onNewProjectClick: PropTypes.func,
    onDeployFunctionClick: PropTypes.func,
    onDeployProjectClick: PropTypes.func,
    open: PropTypes.bool,
    parentFolderSubcategory: FolderPropTypes.folderSubcategory,
    use: PropTypes.oneOf(['default', 'contextmenu']),
    fileMenuActionsList: PropTypes.arrayOf(PropTypes.node)
  };
  HighOrderNewAssetActions.defaultProps = {
    canCreateFile: false,
    canCreateFolder: false,
    folderId: null,
    folderName: null,
    folderSubcategory: null,
    onClose: emptyFunction,
    onNewAssetFormShown: emptyFunction,
    onNewFileClick: emptyFunction,
    onNewThemeClick: emptyFunction,
    onNewFunctionClick: emptyFunction,
    onNewProjectClick: emptyFunction,
    onDeployFunctionClick: emptyFunction,
    onDeployProjectClick: emptyFunction,
    open: null,
    parentFolderSubcategory: null,
    use: 'default'
  };
  return compose(withFileMenuActionsList, connect(makeMapStateToProps, mapDispatchToProps))(HighOrderNewAssetActions);
}